import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { 
  ArrowRight, 
  Menu, 
  X, 
  Layers, 
  Globe, 
  Cpu, 
  Zap, 
  Users, 
  CreditCard, 
  BarChart3,
  GraduationCap,
  Video,
  Layout,
  Bot,
  Workflow,
  BarChart2,
  Check,
  ChevronDown,
  MessageSquare,
  Calendar,
  Database,
  Smartphone,
  MoreHorizontal,
  Sparkles,
  Target,
  Handshake,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { CustomCursor, CursorSpotlight, ScrollGlows } from "@/components/AnimationSystem";

// Configurable WhatsApp number
const WHATSAPP_NUMBER = "919475332206";

// WhatsApp message generator
const getWhatsAppLink = (message) => {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
};

// Pre-defined messages for different CTAs
const WHATSAPP_MESSAGES = {
  general: "Hi Growvise, I'd like to learn more about your services.",
  platformManagement: "Hi Growvise, I'm interested in Platform Management. I'd like to understand the pricing and what's included.",
  aiSolutions: "Hi Growvise, I have a business problem and I'd like to discuss an AI or digital solution.",
  websiteDevelopment: "Hi Growvise, I'd like to get a quote for a website or digital development project.",
  setup: "Hi Growvise, I'm interested in Growvise Setup. I'd like to know the pricing and what's included.",
  manage: "Hi Growvise, I'm interested in Growvise Manage. I'd like to understand the pricing and inclusions.",
  managePro: "Hi Growvise, I'm interested in Growvise Manage Pro. I'd like to understand the pricing and inclusions.",
  build: "Hi Growvise, I have a digital solution I'd like Growvise to build.",
  website: "Hi Growvise, I'm interested in getting a custom website built.",
  landingPage: "Hi Growvise, I need a high-converting landing page for my business.",
  platformMigration: "Hi Growvise, I'd like to migrate my platform. Can we discuss the process?",
  customerSupport: "Hi Growvise, I'm interested in your customer support services.",
  additionalPlatform: "Hi Growvise, I need help managing an additional platform.",
  customAutomation: "Hi Growvise, I'd like to discuss custom automation for my business.",
  consultation: "Hi Growvise, I'd like to book a consultation to discuss my digital business needs.",
};

const LOGO_FULL = "https://tagmango.com/assets/growvise-full-logo-406f53416f6310c4ef90a18a21b0f37f.png";
const LOGO_ICON = "https://tagmango.com/assets/g-logo-9523ebaf456059b33e538f2498beb230.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Work With Us", href: "#work-with-us" },
    { name: "About", href: "#about" },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[#08090C]/95 backdrop-blur-xl border-b border-[#242832]/80 py-0"
          : "bg-transparent py-1"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-14 lg:h-16' : 'h-16 lg:h-20'}`}>
          <a 
            href="/" 
            onClick={handleLogoClick}
            className="flex items-center" 
            data-testid="logo-link"
          >
            <img
              src={LOGO_FULL}
              alt="Growvise - Your Digital Business, Managed"
              className={`transition-all duration-300 ${isScrolled ? 'h-7 lg:h-8' : 'h-8 lg:h-10'} w-auto`}
            />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-[#9CA3AF] hover:text-[#F5F7FA] transition-colors relative group"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#2563FF] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <Dialog open={isQuoteOpen} onOpenChange={setIsQuoteOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="nav-cta-get-quote"
                  className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Get a Quote <ArrowRight className="w-4 h-4 arrow-icon" />
                  </span>
                </Button>
              </DialogTrigger>
              <QuoteModal onClose={() => setIsQuoteOpen(false)} />
            </Dialog>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#F5F7FA] hover:bg-[#242832]/50 rounded-lg transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden bg-[#101217]/98 backdrop-blur-xl border-t border-[#242832] rounded-b-2xl overflow-hidden"
            >
              <div className="py-4 px-4 space-y-1">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="block py-3 px-3 text-[#9CA3AF] hover:text-[#F5F7FA] hover:bg-[#242832]/30 rounded-lg transition-all"
                    onClick={(e) => handleNavClick(e, link.href)}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="pt-3"
                >
                  <Dialog open={isQuoteOpen} onOpenChange={setIsQuoteOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="mobile-cta-get-quote"
                        className="w-full btn-primary py-3 rounded-full text-sm font-semibold text-white"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          Get a Quote <ArrowRight className="w-4 h-4" />
                        </span>
                      </Button>
                    </DialogTrigger>
                    <QuoteModal onClose={() => { setIsQuoteOpen(false); setIsMobileMenuOpen(false); }} />
                  </Dialog>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

const QuoteModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    services: [],
    message: "",
  });

  const services = [
    "Platform Management",
    "AI & Digital Solutions",
    "Website & Digital Development",
  ];

  const handleServiceToggle = (service) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `Hi Growvise Team,

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Services Interested: ${formData.services.join(", ") || "Not specified"}

Message: ${formData.message}`;

    window.open(getWhatsAppLink(message), "_blank");
    toast.success("Redirecting to WhatsApp...");
    onClose();
  };

  return (
    <DialogContent className="bg-[#101217] border border-[#242832] text-[#F5F7FA] max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold">Get a Quote</DialogTitle>
        <DialogDescription className="text-sm text-[#9CA3AF]">
          Fill out the form below and we'll connect with you on WhatsApp.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-5 mt-4">
        <div>
          <Label htmlFor="name" className="text-sm text-[#9CA3AF]">
            Full Name
          </Label>
          <Input
            id="name"
            data-testid="quote-input-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1.5 bg-[#08090C] border-[#242832] text-[#F5F7FA] focus:border-[#2563FF]"
            placeholder="Your name"
            required
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm text-[#9CA3AF]">
            Business Email
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="quote-input-email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1.5 bg-[#08090C] border-[#242832] text-[#F5F7FA] focus:border-[#2563FF]"
            placeholder="you@company.com"
            required
          />
        </div>
        <div>
          <Label htmlFor="company" className="text-sm text-[#9CA3AF]">
            Company / Brand Name
          </Label>
          <Input
            id="company"
            data-testid="quote-input-company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="mt-1.5 bg-[#08090C] border-[#242832] text-[#F5F7FA] focus:border-[#2563FF]"
            placeholder="Your company"
          />
        </div>
        <div>
          <Label className="text-sm text-[#9CA3AF]">Services Needed</Label>
          <div className="mt-2 space-y-2" data-testid="quote-checkbox-services">
            {services.map((service) => (
              <div key={service} className="flex items-center gap-3">
                <Checkbox
                  id={service}
                  checked={formData.services.includes(service)}
                  onCheckedChange={() => handleServiceToggle(service)}
                  className="border-[#242832] data-[state=checked]:bg-[#2563FF] data-[state=checked]:border-[#2563FF]"
                />
                <Label
                  htmlFor={service}
                  className="text-sm text-[#9CA3AF] cursor-pointer"
                >
                  {service}
                </Label>
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="message" className="text-sm text-[#9CA3AF]">
            Tell us about your needs
          </Label>
          <Textarea
            id="message"
            data-testid="quote-input-message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="mt-1.5 bg-[#08090C] border-[#242832] text-[#F5F7FA] focus:border-[#2563FF] min-h-[100px]"
            placeholder="Describe your digital infrastructure needs..."
          />
        </div>
        <Button
          type="submit"
          data-testid="quote-form-submit"
          className="w-full btn-primary py-3 rounded-full text-white font-semibold"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Send via WhatsApp
          </span>
        </Button>
      </form>
    </DialogContent>
  );
};

const EcosystemVisualization = () => {
  // 7 nodes evenly distributed around the circle (360/7 = ~51.43° apart)
  // Starting from top (-90°) and going clockwise
  const nodeCount = 7;
  const angleStep = 360 / nodeCount;
  const startAngle = -90; // Start from top
  
  const nodes = [
    { name: "Platforms", icon: Layers },
    { name: "Websites", icon: Globe },
    { name: "AI Tools", icon: Cpu },
    { name: "Automation", icon: Zap },
    { name: "Payments", icon: CreditCard },
    { name: "Customers", icon: Users },
    { name: "Data", icon: BarChart3 },
  ].map((node, index) => ({
    ...node,
    angle: startAngle + (index * angleStep)
  }));

  const radius = 140;
  const centerX = 200;
  const centerY = 200;

  return (
    <div className="relative w-full max-w-[400px] h-[400px] mx-auto ecosystem-visual">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563FF" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#00B8FF" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#2563FF" stopOpacity="0.2" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {nodes.map((node, index) => {
          const angleRad = (node.angle * Math.PI) / 180;
          const x = centerX + radius * Math.cos(angleRad);
          const y = centerY + radius * Math.sin(angleRad);
          return (
            <line
              key={`line-${index}`}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="url(#lineGradient)"
              strokeWidth="2"
              className="connection-line"
              filter="url(#glow)"
              style={{ animationDelay: `${index * 0.2}s` }}
            />
          );
        })}

        <circle
          cx={centerX}
          cy={centerY}
          r="50"
          fill="#101217"
          stroke="#2563FF"
          strokeWidth="2"
          className="ecosystem-center"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r="60"
          fill="none"
          stroke="#2563FF"
          strokeWidth="1"
          strokeOpacity="0.3"
          className="animate-pulse-glow"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r="70"
          fill="none"
          stroke="#00B8FF"
          strokeWidth="1"
          strokeOpacity="0.2"
          className="animate-pulse-glow"
          style={{ animationDelay: "0.5s" }}
        />
      </svg>

      {/* Centered G Logo */}
      <div
        className="absolute transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
        style={{ left: centerX, top: centerY, width: 100, height: 100 }}
      >
        <img
          src={LOGO_ICON}
          alt="Growvise"
          className="w-16 h-16 object-contain"
        />
      </div>

      {nodes.map((node, index) => {
        const angleRad = (node.angle * Math.PI) / 180;
        const x = centerX + radius * Math.cos(angleRad);
        const y = centerY + radius * Math.sin(angleRad);
        const Icon = node.icon;

        return (
          <motion.div
            key={node.name}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 ecosystem-node"
            style={{ left: x, top: y }}
          >
            <div className="flex flex-col items-center gap-1">
              <div className="w-12 h-12 rounded-xl bg-[#101217] border border-[#242832] flex items-center justify-center hover:border-[#00B8FF] transition-all">
                <Icon className="w-5 h-5 text-[#00B8FF]" />
              </div>
              <span className="text-[10px] text-[#9CA3AF] font-medium whitespace-nowrap">
                {node.name}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const HeroSection = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      style={{ contain: 'paint' }}
    >
      {/* Animated background glows */}
      <div className="hero-glow" />
      <div className="hero-glow-2" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(37,99,255,0.1)_0%,_transparent_70%)] pointer-events-none" />
      <div className="absolute top-1/4 right-0 w-72 md:w-96 h-72 md:h-96 bg-[#2563FF]/10 rounded-full blur-[100px] md:blur-[120px] animate-pulse-glow pointer-events-none" style={{ maxWidth: '40vw' }} />
      <div className="absolute bottom-1/4 left-0 w-48 md:w-72 h-48 md:h-72 bg-[#00B8FF]/10 rounded-full blur-[80px] md:blur-[100px] animate-pulse-glow pointer-events-none" style={{ animationDelay: '1s', maxWidth: '30vw' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center lg:text-left"
          >
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-block text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00B8FF] mb-6"
            >
              Digital Business Solutions
            </motion.span>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#F5F7FA] mb-6 text-balance"
            >
              Your Digital Business,{" "}
              <span className="gradient-text">Managed.</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-base lg:text-lg text-[#9CA3AF] mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              Growvise builds and manages the digital infrastructure behind your
              business — from platforms and websites to AI-powered tools and
              digital solutions.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a
                href={getWhatsAppLink(WHATSAPP_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
              >
                <Button className="btn-primary px-8 py-6 rounded-full text-base font-semibold text-white w-full sm:w-auto">
                  <span className="relative z-10 flex items-center gap-2">
                    Get Started <ArrowRight className="w-5 h-5 arrow-icon" />
                  </span>
                </Button>
              </a>
              <a 
                href="#services" 
                data-testid="hero-cta-explore-services"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <Button className="btn-secondary px-8 py-6 rounded-full text-base font-medium text-[#F5F7FA] w-full sm:w-auto">
                  <span className="flex items-center gap-1">
                    Explore Services <ChevronDown className="w-5 h-5" />
                  </span>
                </Button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <EcosystemVisualization />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = () => {
  const problems = [
    {
      title: "Course Platforms",
      icon: GraduationCap,
      desc: "Fragmented student access, broken video embeds, tier lockouts.",
    },
    {
      title: "Webinar Engines",
      icon: Video,
      desc: "Attendance tracking drops, email reminder failures, live stream lag.",
    },
    {
      title: "Payment Stack",
      icon: CreditCard,
      desc: "Failed webhook billing, multi-currency chaos, tax compliance issues.",
    },
    {
      title: "Customer Ops",
      icon: Users,
      desc: "Manual onboarding, lost support tickets, disconnected community portals.",
    },
    {
      title: "Custom Portals",
      icon: Layout,
      desc: "Outdated frontend UI, slow page load times, mobile unresponsiveness.",
    },
    {
      title: "AI & Chatbots",
      icon: Bot,
      desc: "Hallucinating customer bots, lack of CRM sync, unintegrated workflows.",
    },
    {
      title: "Workflow Automation",
      icon: Workflow,
      desc: "Broken Zapier triggers, duplicate records, unmonitored API errors.",
    },
    {
      title: "Data & Analytics",
      icon: BarChart2,
      desc: "Siloed revenue metrics, inaccurate conversion tracking, manual reports.",
    },
  ];

  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="py-20 lg:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA] mb-6">
            You built the business.{" "}
            <span className="text-[#00B8FF]">
              Why are you still managing the backend?
            </span>
          </h2>
          <p className="text-base text-[#9CA3AF] leading-relaxed">
            Managing digital businesses requires constantly juggling platforms,
            webinars, courses, payment gateways, automation hooks, and custom
            websites. It drains founder focus.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                data-testid={`problem-card-${index}`}
                className="problem-card rounded-xl p-5"
              >
                <div className="icon-wrapper w-11 h-11 rounded-lg bg-[#2563FF]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#2563FF]" />
                </div>
                <h3 className="text-base font-semibold text-[#F5F7FA] mb-2">
                  {problem.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">
                  {problem.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-xl lg:text-2xl font-semibold text-[#F5F7FA] mt-16"
        >
          That's where{" "}
          <span className="text-[#00B8FF]">Growvise</span> comes in.
        </motion.p>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const services = [
    {
      number: "01",
      title: "Platform Management",
      headline: "We run your digital platform.",
      description: "From setup to ongoing operations, Growvise manages the digital platforms behind your business.",
      platforms: "TagMango · Graphy · Kajabi · Exly · and more",
      features: [
        "Course management",
        "Webinar management",
        "Product management",
        "Customer/student management",
        "Payment setup",
        "Email and WhatsApp automation",
        "Platform operations",
        "Troubleshooting",
      ],
      isFlagship: true,
    },
    {
      number: "02",
      title: "AI & Digital Solutions",
      headline: "We build the tools your business needs.",
      description: "We build custom AI-powered tools, dashboards, portals, automation and digital systems around specific business problems.",
      features: [
        "AI-powered tools",
        "Dashboards",
        "Portals",
        "Business automation",
        "Internal tools",
        "Custom digital solutions",
      ],
      isFlagship: false,
    },
    {
      number: "03",
      title: "Website & Digital Development",
      headline: "We build your digital presence.",
      description: "We build websites, landing pages, sales pages and other digital experiences for digital businesses.",
      features: [
        "Websites",
        "Landing pages",
        "Sales pages",
        "Digital portals",
        "Digital experiences",
      ],
      isFlagship: false,
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 lg:py-28 relative bg-[#0A0B0F]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,255,0.05)_0%,_transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA]">
            What Growvise Does
          </h2>
        </motion.div>

        {/* Flagship Service - Platform Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="service-card-flagship"
          className="mb-8"
        >
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#2563FF]/20 via-[#101217] to-[#00B8FF]/10" />
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2563FF] via-[#00B8FF] to-[#2563FF]" />
            <div className="relative card-surface border border-[#2563FF]/30 rounded-2xl p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="step-number text-5xl lg:text-6xl font-extrabold">01</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#2563FF]/50 to-transparent" />
                  </div>
                  <span className="inline-block text-xs font-bold uppercase tracking-[0.15em] text-[#00B8FF] bg-[#00B8FF]/10 px-3 py-1 rounded-full mb-4">
                    Flagship Service
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#F5F7FA] mb-3">
                    Platform Management
                  </h3>
                  <p className="text-lg lg:text-xl font-semibold text-[#F5F7FA] mb-4">
                    {services[0].headline}
                  </p>
                  <p className="text-base text-[#9CA3AF] mb-6 leading-relaxed">
                    {services[0].description}
                  </p>
                  <p className="text-sm text-[#6B7280] mb-8">
                    <span className="text-[#9CA3AF]">Platforms:</span> {services[0].platforms}
                  </p>
                  <a href={getWhatsAppLink(WHATSAPP_MESSAGES.platformManagement)} target="_blank" rel="noopener noreferrer">
                    <Button 
                      data-testid="service-cta-platform"
                      className="btn-primary px-8 py-4 rounded-full text-base font-semibold text-white"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        Get a Quote <ArrowRight className="w-5 h-5" />
                      </span>
                    </Button>
                  </a>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {services[0].features.map((feature, i) => (
                    <div 
                      key={i} 
                      className="flex items-center gap-3 bg-[#08090C]/60 border border-[#242832] rounded-lg px-4 py-3"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#00B8FF] flex-shrink-0" />
                      <span className="text-sm text-[#9CA3AF]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Other Services */}
        <div className="grid lg:grid-cols-2 gap-6">
          {services.slice(1).map((service, index) => {
            const waMessage = index === 0 ? WHATSAPP_MESSAGES.aiSolutions : WHATSAPP_MESSAGES.websiteDevelopment;
            return (
            <motion.div
              key={service.number}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`service-card-${index + 1}`}
              className="service-card card-surface rounded-2xl p-6 lg:p-8 card-surface-hover border border-[#242832]"
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="step-number text-4xl lg:text-5xl font-extrabold">
                  {service.number}
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-[#242832] to-transparent" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-[#F5F7FA] mb-2">
                {service.title}
              </h3>
              <p className="text-base font-semibold text-[#F5F7FA] mb-3">
                {service.headline}
              </p>
              <p className="text-sm text-[#9CA3AF] mb-6 leading-relaxed">
                {service.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {service.features.map((feature, i) => (
                  <span 
                    key={i} 
                    className="text-xs text-[#9CA3AF] bg-[#08090C] border border-[#242832] px-3 py-1.5 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              <a href={getWhatsAppLink(waMessage)} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid={`service-cta-${index + 1}`}
                  className="btn-secondary px-6 py-3 rounded-full text-sm font-medium text-[#F5F7FA]"
                >
                  Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>
          )})}
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      step: "01",
      title: "Digital Audit & Architecture Map",
      desc: "We analyze your current digital ecosystem, identifying bottlenecks, manual overhead, and broken technical links.",
    },
    {
      step: "02",
      title: "Infrastructure Modernization",
      desc: "Our team deploys robust digital solutions, builds custom portals, and wires up automated AI workflows.",
    },
    {
      step: "03",
      title: "Continuous Growth & Operations",
      desc: "Growvise takes over day-to-day platform management, maintenance, and technical optimization so you focus on scale.",
    },
  ];

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-20 lg:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00B8FF] mb-4 block">
            Our Process
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA]">
            How It Works
          </h2>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#242832] to-transparent" />

          <div className="grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                data-testid={`step-card-${index}`}
                className="relative text-center lg:text-left"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#101217] border border-[#242832] mb-6 mx-auto lg:mx-0">
                  <span className="step-number text-2xl font-extrabold">
                    {step.step}
                  </span>
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-[#F5F7FA] mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-[#9CA3AF] leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WorkWithUsSection = () => {
  const plans = [
    {
      name: "Setup",
      tagline: "One-time digital platform setup.",
      description: "Includes platform configuration, payments, courses, webinars, WhatsApp automation, email automation, testing and launch.",
      highlight: false,
      waMessage: WHATSAPP_MESSAGES.setup,
    },
    {
      name: "Manage",
      tagline: "Ongoing digital platform management.",
      description: "Includes platform management, courses, webinars, products, customers and platform operations.",
      highlight: false,
      waMessage: WHATSAPP_MESSAGES.manage,
    },
    {
      name: "Manage Pro",
      tagline: "Your digital operations partner.",
      description: "Includes everything in Manage plus website/landing-page work, customer support, AI/digital solutions and broader digital operations.",
      highlight: true,
      waMessage: WHATSAPP_MESSAGES.managePro,
    },
    {
      name: "Build",
      tagline: "Custom digital solutions.",
      description: "For AI tools, dashboards, portals, automation, websites and custom digital systems.",
      highlight: false,
      waMessage: WHATSAPP_MESSAGES.build,
    },
  ];

  return (
    <section
      id="work-with-us"
      data-testid="work-with-us-section"
      className="py-20 lg:py-28 relative bg-[#0A0B0F]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(0,184,255,0.05)_0%,_transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA]">
            Choose How You Want Growvise to Work With You.
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-sm text-[#6B7280] mb-12 max-w-2xl mx-auto"
        >
          Pricing is customized based on your specific requirements. Get in touch for a personalized quote.
        </motion.p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              data-testid={`work-plan-${index}`}
              className={`relative rounded-2xl p-6 transition-all duration-300 ${
                plan.highlight 
                  ? "bg-gradient-to-b from-[#101217] to-[#0A0B0F] border-2 border-[#2563FF] shadow-[0_0_40px_rgba(37,99,255,0.15)]" 
                  : "card-surface border border-[#242832] hover:border-[#2563FF]/50"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#2563FF] text-white px-3 py-1 rounded-full">
                    Popular
                  </span>
                </div>
              )}
              
              <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? "text-[#00B8FF]" : "text-[#F5F7FA]"}`}>
                {plan.name}
              </h3>
              <p className="text-sm font-medium text-[#F5F7FA] mb-3">
                {plan.tagline}
              </p>
              <p className="text-sm text-[#6B7280] mb-6 leading-relaxed min-h-[60px]">
                {plan.description}
              </p>
              
              <a href={getWhatsAppLink(plan.waMessage)} target="_blank" rel="noopener noreferrer" className="block">
                <Button 
                  data-testid={`work-plan-cta-${index}`}
                  className={`w-full rounded-full text-sm font-medium ${
                    plan.highlight 
                      ? "btn-primary text-white" 
                      : "btn-secondary text-[#F5F7FA]"
                  }`}
                >
                  <span className={plan.highlight ? "relative z-10 flex items-center justify-center gap-2" : "flex items-center justify-center gap-2"}>
                    Get a Quote <ArrowRight className="w-4 h-4" />
                  </span>
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AddOnsSection = () => {
  const addons = [
    { name: "Website", desc: "Custom business website", waMessage: WHATSAPP_MESSAGES.website },
    { name: "Landing Page", desc: "High-converting landing page", waMessage: WHATSAPP_MESSAGES.landingPage },
    { name: "Platform Migration", desc: "Migrate to a new platform", waMessage: WHATSAPP_MESSAGES.platformMigration },
    { name: "Customer Support", desc: "Dedicated support handling", waMessage: WHATSAPP_MESSAGES.customerSupport },
    { name: "Additional Platform", desc: "Manage another platform", waMessage: WHATSAPP_MESSAGES.additionalPlatform },
    { name: "Custom Automation", desc: "Bespoke workflow automation", waMessage: WHATSAPP_MESSAGES.customAutomation },
  ];

  return (
    <section
      id="add-ons"
      data-testid="add-ons-section"
      className="py-20 lg:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA] mb-4">
            Need More? Add What You Need.
          </h2>
          <p className="text-sm text-[#6B7280] max-w-xl mx-auto">
            Extend your engagement with additional services. Pricing based on scope and requirements.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {addons.map((addon, index) => (
            <motion.div
              key={addon.name}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              data-testid={`addon-card-${index}`}
              className="group flex items-center justify-between gap-4 p-5 rounded-xl bg-[#101217]/50 border border-[#242832] hover:border-[#2563FF]/50 transition-all duration-300 hover:bg-[#101217]"
            >
              <div className="flex-1">
                <h3 className="text-base font-semibold text-[#F5F7FA] mb-1 group-hover:text-[#00B8FF] transition-colors">
                  {addon.name}
                </h3>
                <p className="text-xs text-[#6B7280]">{addon.desc}</p>
              </div>
              <a href={getWhatsAppLink(addon.waMessage)} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid={`addon-cta-${index}`}
                  size="sm"
                  className="btn-secondary px-4 py-2 rounded-full text-xs font-medium text-[#9CA3AF] group-hover:text-[#F5F7FA] group-hover:border-[#2563FF]/50"
                >
                  Get a Quote <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyGrowviseSection = () => {
  const reasons = [
    {
      title: "One Digital Partner",
      desc: "Stop juggling multiple freelancers and agencies. Growvise handles your entire digital infrastructure under one roof.",
      icon: Target,
    },
    {
      title: "End-to-End Execution",
      desc: "From strategy to implementation to ongoing operations. We don't just advise — we execute and deliver results.",
      icon: Sparkles,
    },
    {
      title: "Built Around Your Business",
      desc: "Every solution is customized to your specific workflows, goals, and growth trajectory. No cookie-cutter templates.",
      icon: Handshake,
    },
    {
      title: "Works Alongside Your Team",
      desc: "We integrate seamlessly with your existing team, filling gaps and amplifying capabilities without friction.",
      icon: UserCheck,
    },
  ];

  return (
    <section
      id="why-growvise"
      data-testid="why-growvise-section"
      className="py-20 lg:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA]">
            Not Another Freelancer. Not Another Agency.
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`why-card-${index}`}
                className="why-card rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-[#2563FF]/10 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[#2563FF]" />
                </div>
                <h3 className="text-base lg:text-lg font-semibold text-[#F5F7FA] mb-2">
                  {reason.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">
                  {reason.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WhoWeHelpSection = () => {
  const audiences = [
    { name: "Coaches", icon: Users },
    { name: "Course Creators", icon: GraduationCap },
    { name: "Educators", icon: Video },
    { name: "Consultants", icon: MessageSquare },
    { name: "Digital Businesses", icon: Globe },
  ];

  return (
    <section
      id="who-we-help"
      data-testid="who-we-help-section"
      className="py-20 lg:py-28 relative bg-[#0A0B0F]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA]">
            Built for Digital Businesses.
          </h2>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {audiences.map((audience, index) => {
            const Icon = audience.icon;
            return (
              <motion.div
                key={audience.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                data-testid={`audience-${index}`}
                className="flex items-center gap-3 px-6 py-4 rounded-full bg-[#101217] border border-[#242832] hover:border-[#2563FF]/50 transition-all duration-300"
              >
                <Icon className="w-5 h-5 text-[#00B8FF]" />
                <span className="text-sm lg:text-base font-medium text-[#F5F7FA]">
                  {audience.name}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const VisionSection = () => {
  const ecosystemItems = [
    { name: "Platforms", icon: Layers },
    { name: "Websites", icon: Globe },
    { name: "AI", icon: Cpu },
    { name: "Automation", icon: Zap },
    { name: "Data", icon: Database },
    { name: "Apps", icon: Smartphone },
    { name: "More", icon: MoreHorizontal },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 lg:py-28 relative"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,255,0.08)_0%,_transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA] mb-6">
            One Partner. Everything Digital.
          </h2>
          <p className="text-base lg:text-lg text-[#9CA3AF] max-w-3xl mx-auto leading-relaxed">
            Growvise is building toward becoming a one-stop digital partner for businesses. 
            Our current focus is platform management, AI & digital solutions, and website development — 
            with a roadmap to expand across every digital touchpoint your business needs.
          </p>
        </motion.div>

        {/* Ecosystem Visual */}
        <div className="relative max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
            {ecosystemItems.map((item, index) => {
              const Icon = item.icon;
              const isLast = index === ecosystemItems.length - 1;
              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      index < 3 
                        ? "bg-[#2563FF]/20 border border-[#2563FF]/40" 
                        : "bg-[#101217] border border-[#242832] hover:border-[#2563FF]/30"
                    }`}>
                      <Icon className={`w-6 h-6 md:w-7 md:h-7 ${index < 3 ? "text-[#00B8FF]" : "text-[#6B7280]"}`} />
                    </div>
                    <span className={`text-xs font-medium ${index < 3 ? "text-[#F5F7FA]" : "text-[#6B7280]"}`}>
                      {item.name}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="hidden sm:flex items-center mx-2 md:mx-3">
                      <ArrowRight className={`w-4 h-4 ${index < 2 ? "text-[#2563FF]" : "text-[#242832]"}`} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center text-sm text-[#6B7280] mt-8"
          >
            <span className="text-[#00B8FF]">Currently active</span> · Roadmap expanding
          </motion.p>
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  
  return (
    <section
      ref={sectionRef}
      id="final-cta"
      data-testid="final-cta-section"
      className={`py-20 lg:py-28 relative overflow-hidden ${isInView ? 'final-cta-visible' : ''}`}
    >
      <div className="final-cta-glow" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(37,99,255,0.15)_0%,_transparent_60%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F5F7FA] mb-4">
            You Focus on the Business. We'll Handle the Digital.
          </h2>
          <p className="text-base lg:text-lg text-[#9CA3AF] mb-8 max-w-2xl mx-auto">
            Tell us what you're building, what's slowing you down, and what you need help with.
          </p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href={getWhatsAppLink(WHATSAPP_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
            >
              <Button className="btn-primary px-8 py-6 rounded-full text-base font-semibold text-white w-full sm:w-auto">
                <span className="relative z-10 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Get a Quote on WhatsApp
                  <ArrowRight className="w-4 h-4 arrow-icon" />
                </span>
              </Button>
            </a>
            <a
              href={getWhatsAppLink(WHATSAPP_MESSAGES.consultation)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-consultation"
            >
              <Button className="btn-secondary px-8 py-6 rounded-full text-base font-medium text-[#F5F7FA] w-full sm:w-auto">
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Book a Consultation
                </span>
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Footer = () => {
  const links = [
    { name: "Services", href: "#services" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Work With Us", href: "#work-with-us" },
    { name: "About", href: "#about" },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer
      data-testid="footer"
      className="py-12 lg:py-16 border-t border-[#242832] bg-[#08090C]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center lg:items-start gap-3">
            <a href="/" onClick={handleLogoClick}>
              <img
                src={LOGO_FULL}
                alt="Growvise"
                className="h-8 w-auto hover:opacity-80 transition-opacity"
              />
            </a>
            <p className="text-sm text-[#6B7280]">
              Premium Digital Business Solutions
            </p>
          </div>

          <nav className="flex flex-wrap justify-center gap-6" aria-label="Footer navigation">
            {links.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="footer-link text-sm text-[#9CA3AF]"
              >
                {link.name}
              </a>
            ))}
          </nav>

          <p className="text-sm text-[#6B7280]">
            © {new Date().getFullYear()} Growvise. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-[#08090C] relative">
      {/* Animation System */}
      <CustomCursor />
      <CursorSpotlight />
      <ScrollGlows />
      
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#101217",
            border: "1px solid #242832",
            color: "#F5F7FA",
          },
        }}
      />
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <ServicesSection />
        <HowItWorksSection />
        <WorkWithUsSection />
        <AddOnsSection />
        <WhyGrowviseSection />
        <WhoWeHelpSection />
        <VisionSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
